/* --- File: src/components/NotificationPopupModal.js ---
   Modal popup thông báo bắt buộc - chặn mọi thao tác cho đến khi xác nhận
*/
import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography, Tag, Input } from 'antd';
import { AlertOutlined, FileTextOutlined, TrophyOutlined, MailOutlined, BellOutlined, ExclamationCircleOutlined, CheckCircleOutlined, ClockCircleOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import { db } from '../firebaseConfig';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';

const { Title, Text } = Typography;
const { TextArea } = Input;

// ── Màu sắc dịu nhẹ theo loại ──
const TYPE_CONFIG = {
    intervention: {
        color: '#a855f7',
        bg: '#f3e8ff',
        icon: <AlertOutlined />,
        label: 'Can thiệp học tập',
    },
    parent_call: {
        color: '#8b5cf6',
        bg: '#ede9fe',
        icon: <AlertOutlined />,
        label: 'Gọi điện phụ huynh',
    },
    meeting: {
        color: '#2563eb',
        bg: '#dbeafe',
        icon: <AlertOutlined />,
        label: 'Gặp trực tiếp',
    },
    tutoring: {
        color: '#059669',
        bg: '#dcfce7',
        icon: <AlertOutlined />,
        label: 'Phụ đạo',
    },
    warning: {
        color: '#ea580c',
        bg: '#fed7aa',
        icon: <ExclamationCircleOutlined />,
        label: 'Cảnh báo',
    },
    email: {
        color: '#0891b2',
        bg: '#cffafe',
        icon: <MailOutlined />,
        label: 'Email/Gửi thư',
    },
    other: {
        color: '#64748b',
        bg: '#f1f5f9',
        icon: <BellOutlined />,
        label: 'Khác',
    },
    risk: {
        color: '#ea580c',
        bg: '#fed7aa',
        icon: <ExclamationCircleOutlined />,
        label: 'Cảnh báo nguy cơ',
    },
    assignment: {
        color: '#059669',
        bg: '#bbf7d0',
        icon: <FileTextOutlined />,
        label: 'Bài tập mới',
    },
    grade: {
        color: '#059669',
        bg: '#bbf7d0',
        icon: <TrophyOutlined />,
        label: 'Kết quả học tập',
    },
    exam: {
        color: '#ca8a04',
        bg: '#fef08a',
        icon: <BellOutlined />,
        label: 'Thông tin thi',
    },
    alert: {
        color: '#ca8a04',
        bg: '#fef08a',
        icon: <ClockCircleOutlined />,
        label: 'Nhắc nhở',
    },
    system: {
        color: '#7c3aed',
        bg: '#ddd6fe',
        icon: <BellOutlined />,
        label: 'Thông báo hệ thống',
    },
    info: {
        color: '#0284c7',
        bg: '#bae6fd',
        icon: <MailOutlined />,
        label: 'Tin tức',
    },
};

// Helper để format ngày
const formatDate = (date) => {
    if (!date) return '';
    let d;
    if (date && typeof date === 'object' && date.toDate) {
        d = date.toDate();
    } else if (date && typeof date === 'object' && date.seconds) {
        d = new Date(date.seconds * 1000);
    } else {
        d = new Date(date);
    }
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Helper để format giờ
const formatTime = (date) => {
    if (!date) return '';
    let d;
    if (date && typeof date === 'object' && date.toDate) {
        d = date.toDate();
    } else if (date && typeof date === 'object' && date.seconds) {
        d = new Date(date.seconds * 1000);
    } else {
        d = new Date(date);
    }
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
};

// Helper chung để lấy Date object từ bất kỳ dạng nào
const toDate = (val) => {
    if (!val) return null;
    if (val && typeof val === 'object' && val.toDate) return val.toDate();
    if (val && typeof val === 'object' && val.seconds) return new Date(val.seconds * 1000);
    if (val instanceof Date) return val;
    return new Date(val);
};

export default function NotificationPopupModal({ notifications, onClose, caseConfigs }) {
    const [currentIdx, setCurrentIdx] = useState(0);
    const [open, setOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [responseText, setResponseText] = useState('');
    const [debugInfo, setDebugInfo] = useState('');

    const unreadInterventions = notifications.filter(n => {
        const isUnread = !n.isRead;
        const isIntervention = n.type === 'intervention' || n.type === 'risk';
        
        return isUnread && isIntervention;
    });
    const hasUnreadInterventions = unreadInterventions.length > 0;

    // Debug info
    useEffect(() => {
        setDebugInfo(`Có ${notifications.length} notif, ${unreadInterventions.length} can thiệp chưa đọc`);
        
    }, [notifications.length, unreadInterventions.length]);

    // Mở popup khi có thông báo can thiệp chưa đọc - check realtime
    useEffect(() => {
        
        if (hasUnreadInterventions && !open) {
            setOpen(true);
            setCurrentIdx(0);
            setResponseText('');
        }
    }, [hasUnreadInterventions, notifications.length]);

    // Reset khi đóng popup
    useEffect(() => {
        if (!open) {
            setCurrentIdx(0);
            setResponseText('');
        }
    }, [open]);

    const currentNotif = unreadInterventions[currentIdx];
    const config = TYPE_CONFIG[currentNotif?.caseType] || TYPE_CONFIG.intervention;
    const caseData = currentNotif?.caseData || {};

    // Debug
    if (currentNotif) {
        
        
        
    }

    // Kiểm tra case có yêu cầu phản hồi không
    const caseConfig = caseConfigs?.[currentNotif?.caseType];
    const requiresResponse = caseConfig?.requireResponse;
    const responseLabel = caseConfig?.responseLabel || 'Lời hứa / Phản hồi của bạn';
    const responsePlaceholder = caseConfig?.responsePlaceholder || 'Hãy nhập lời hứa hoặc phản hồi của bạn...';

    // Render field value
    const renderFieldValue = (fieldName, fieldType) => {
        const value = caseData[fieldName];
        if (!value) return '—';

        if (fieldType === 'date') return formatDate(value);
        if (fieldType === 'time') return formatTime(value);
        if (fieldType === 'editor') return value;

        return value;
    };

    // Lấy fields theo caseType
    const getFieldsForCaseType = (caseType) => {
        const fieldsMap = {
            parent_call: [
                { name: 'callDate', label: 'Ngày gọi', type: 'date' },
                { name: 'caller', label: 'Người gọi' },
                { name: 'participant', label: 'Người tham dự' },
                { name: 'topic', label: 'Chủ đề' },
                { name: 'summary', label: 'Nội dung cuộc gọi', type: 'editor' },
                { name: 'agreements', label: 'Thỏa thuận đạt được', type: 'editor' },
            ],
            meeting: [
                { name: 'meetingDate', label: 'Ngày họp', type: 'date' },
                { name: 'location', label: 'Địa điểm' },
                { name: 'topic', label: 'Chủ đề' },
                { name: 'summary', label: 'Biên bản cuộc họp', type: 'editor' },
                { name: 'decisions', label: 'Quyết định đạt được', type: 'editor' },
            ],
            tutoring: [
                { name: 'tutorDate', label: 'Ngày phụ đạo', type: 'date' },
                { name: 'duration', label: 'Thời lượng' },
                { name: 'location', label: 'Địa điểm' },
                { name: 'tutor', label: 'Người phụ đạo' },
                { name: 'topics', label: 'Chủ đề ôn tập', type: 'editor' },
                { name: 'result', label: 'Kết quả', type: 'editor' },
            ],
            warning: [
                { name: 'warningType', label: 'Loại cảnh báo' },
                { name: 'violation', label: 'Vi phạm', type: 'editor' },
                { name: 'consequence', label: 'Hậu quả nếu tiếp tục', type: 'editor' },
                { name: 'promise', label: 'Lời hứa của HS', type: 'editor' },
                { name: 'witness', label: 'Người chứng' },
            ],
            email: [
                { name: 'sentDate', label: 'Ngày gửi', type: 'date' },
                { name: 'recipient', label: 'Người nhận' },
                { name: 'subject', label: 'Tiêu đề' },
                { name: 'summary', label: 'Nội dung', type: 'editor' },
            ],
            other: [
                { name: 'actionType', label: 'Loại hành động' },
                { name: 'description', label: 'Mô tả chi tiết', type: 'editor' },
                { name: 'result', label: 'Kết quả', type: 'editor' },
            ],
        };
        return fieldsMap[caseType] || [];
    };

    const fields = getFieldsForCaseType(currentNotif?.caseType);

    const handleConfirm = async () => {
        if (!currentNotif) return;

        // Nếu yêu cầu phản hồi mà chưa nhập
        if (requiresResponse && !responseText.trim()) {
            alert('Vui lòng nhập lời hứa/phản hồi trước khi xác nhận!');
            return;
        }

        setConfirming(true);
        try {
            
            

            // Cập nhật isRead notification
            await updateDoc(doc(db, 'notifications', currentNotif.key), {
                isRead: true,
                readAt: new Date(),
                readBy: currentNotif.receiverId,
            });

            // Nếu có phản hồi, lưu vào case
            if (responseText.trim()) {
                // Tìm case trong Firestore bằng caseId
                const casesQuery = query(
                    collection(db, 'risk_cases'),
                    where('caseId', '==', currentNotif.relatedCaseId)
                );
                const caseSnap = await getDocs(casesQuery);

                if (!caseSnap.empty) {
                    const caseDoc = caseSnap.docs[0];
                    const existingLogs = caseDoc.data().progressLogs || [];
                    const newLog = {
                        id: 'RESP-' + Date.now(),
                        note: `📝 Phản hồi của HS: ${responseText}`,
                        createdBy: currentNotif.receiverName || 'Học sinh',
                        createdAt: new Date(),
                        isStudentResponse: true,
                    };

                    await updateDoc(doc(db, 'risk_cases', caseDoc.id), {
                        studentResponse: responseText,
                        studentResponseAt: new Date(),
                        progressLogs: [...existingLogs, newLog],
                    });

                    
                } else {
                    
                }
            }

            if (currentIdx < unreadInterventions.length - 1) {
                setCurrentIdx(prev => prev + 1);
                setResponseText('');
            } else {
                setOpen(false);
                setResponseText('');
                onClose?.();
            }
        } catch (err) {
            console.error("Lỗi khi lưu phản hồi:", err);
            alert('Có lỗi xảy ra: ' + err.message);
        } finally {
            setConfirming(false);
        }
    };

    if (!currentNotif) return null;

        // Nếu chưa xác nhận hết, bắt buộc hiện popup
    const mustShow = hasUnreadInterventions;

    // Debug display
    if (!mustShow) {
        return (
            <div style={{
                position: 'fixed', top: 10, right: 10, zIndex: 99999,
                background: '#fff', padding: 12, borderRadius: 8,
                boxShadow: "var(--ui-shadow, 0 3px 14px rgba(23,34,56,.045))", fontSize: 12,
                maxWidth: 300
            }}>
                <Text style={{ fontSize: 11, color: '#64748b' }}>{debugInfo}</Text>
                <br />
                <Text style={{ fontSize: 11, color: '#64748b' }}>
                    {unreadInterventions.length === 0 ? '✓ Không có can thiệp chưa đọc' : ''}
                </Text>
            </div>
        );
    }

    return (
        <>
            <style>{`
                @keyframes notifSlideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .notif-popup-wrapper {
                    animation: notifSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
                }
                .notif-blocking-overlay {
                    position: fixed !important;
                    top: 0 !important;
                    left: 0 !important;
                    right: 0 !important;
                    bottom: 0 !important;
                    z-index: 99999 !important;
                    background: rgba(0, 0, 0, 0.5) !important;
                    backdrop-filter: blur(4px) !important;
                }
                .notif-popup-modal .ant-modal-content { border-radius: 16px !important; overflow: hidden; }
                @media (max-width: 768px) {
                    .notif-popup-modal.ant-modal { width: 92vw !important; max-width: 92vw !important; }
                }
            `}</style>

            <Modal
                open={true}
                footer={null}
                closable={false}
                width="50%"
                className="notif-popup-modal"
                centered
                maskClosable={false}
                keyboard={false}
                styles={{
                    mask: { pointerEvents: 'auto' },
                    content: { borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', pointerEvents: 'auto' }
                }}
            >
                <div className="notif-popup-wrapper" style={{ padding: '36px 36px 28px' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: 10,
                                background: config.bg,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: config.color, fontSize: 22,
                            }}>
                                {config.icon}
                            </div>
                            <div>
                                <Tag style={{
                                    background: config.bg,
                                    color: config.color,
                                    border: 'none',
                                    borderRadius: 16,
                                    padding: '2px 10px',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    marginBottom: 4,
                                }}>
                                    {config.label}
                                </Tag>
                                <div style={{ fontSize: 12, color: '#64748b' }}>
                                    <UserOutlined style={{ marginRight: 4 }} />
                                    {currentNotif?.senderName || 'Giáo viên'}
                                </div>
                            </div>
                        </div>
                        {unreadInterventions.length > 1 && (
                            <Tag style={{
                                background: '#f1f5f9',
                                color: '#64748b',
                                border: 'none',
                                borderRadius: 10,
                                fontSize: 12,
                            }}>
                                {currentIdx + 1} / {unreadInterventions.length}
                            </Tag>
                        )}
                    </div>

                    {/* Case Details Card */}
                    {fields.length > 0 && (
                        <div style={{
                            background: '#f8fafc',
                            borderRadius: 10,
                            padding: 18,
                            marginBottom: 16,
                            maxHeight: 380,
                            overflowY: 'auto',
                        }}>
                            {Object.keys(caseData).length === 0 ? (
                                <Text style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', display: 'block' }}>
                                    (Không có chi tiết - case cũ. Tạo case mới để xem đầy đủ thông tin)
                                </Text>
                            ) : (
                                fields.map((field) => {
                                    const value = caseData[field.name];
                                    return (
                                        <div key={field.name} style={{ marginBottom: field.type === 'editor' ? 12 : 8 }}>
                                            <Text style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 2 }}>
                                                {field.label}
                                            </Text>
                                            {field.type === 'editor' ? (
                                                value ? (
                                                    <div
                                                        style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.6 }}
                                                        dangerouslySetInnerHTML={{ __html: value }}
                                                    />
                                                ) : (
                                                    <Text type="secondary">—</Text>
                                                )
                                            ) : (
                                                <Text strong style={{ fontSize: 13, color: '#1e293b' }}>
                                                    {renderFieldValue(field.name, field.type)}
                                                </Text>
                                            )}
                                            {field.name !== fields[fields.length - 1].name && (
                                                <div style={{ borderBottom: '1px dashed #e2e8f0', marginTop: 8 }} />
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}

                    {/* Yêu cầu phản hồi */}
                    {requiresResponse && (
                        <div style={{
                            marginBottom: 16,
                            padding: 16,
                            background: '#fef9c3',
                            borderRadius: 10,
                            border: '1px solid #fde047',
                        }}>
                            <Text strong style={{ fontSize: 13, color: '#a16207', display: 'block', marginBottom: 8 }}>
                                <EditOutlined style={{ marginRight: 6 }} />
                                {responseLabel}
                            </Text>
                            <TextArea
                                value={responseText}
                                onChange={(e) => setResponseText(e.target.value)}
                                placeholder={responsePlaceholder}
                                rows={3}
                                style={{ borderRadius: 8, fontSize: 13 }}
                            />
                        </div>
                    )}

                    {/* Pagination */}
                    {unreadInterventions.length > 1 && (
                        <Text style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 16, textAlign: 'center' }}>
                            {currentIdx + 1} / {unreadInterventions.length}
                        </Text>
                    )}

                    {/* Button */}
                    <Button
                        type="primary"
                        block
                        size="large"
                        icon={<CheckCircleOutlined />}
                        loading={confirming}
                        onClick={handleConfirm}
                        style={{
                            height: 44,
                            borderRadius: 10,
                            background: config.color,
                            border: 'none',
                            fontWeight: 600,
                            fontSize: 14,
                            marginTop: 8,
                        }}
                    >
                        {currentIdx < unreadInterventions.length - 1 ? 'Đã đọc, tiếp theo' : 'Xác nhận đã đọc'}
                    </Button>
                </div>
            </Modal>
        </>
    );
}